import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";

// Current tutorial spec version. Bump when the step schema materially
// changes (a step is added/removed/reordered). On load, if the server
// version is lower than this, the client marks progress as stale and
// resets completedStepIds locally before the next PUT.
export const CURRENT_TUTORIAL_VERSION = 1;

const LOCALSTORAGE_KEY = "tutorial_progress_v1";

export interface TutorialProgressState {
  version: number;
  completedStepIds: string[];
  createdViaTutorialOrderIds: number[];
  createdViaTutorialGuestIds: number[];
  startedAt: string | null;
  finishedAt: string | null;
}

const EMPTY_STATE: TutorialProgressState = {
  version: CURRENT_TUTORIAL_VERSION,
  completedStepIds: [],
  createdViaTutorialOrderIds: [],
  createdViaTutorialGuestIds: [],
  startedAt: null,
  finishedAt: null,
};

export interface UseTutorialProgressOptions {
  // Base API URL, e.g. process.env.REACT_APP_API_BASE. Defaults to the
  // relative path so the SPA's default axios base URL is used.
  apiBaseUrl?: string;
  // If false, disables the network calls entirely and keeps state in
  // localStorage only. Useful for offline demos or tests.
  enableRemote?: boolean;
  // If true, when the server-returned version is lower than
  // CURRENT_TUTORIAL_VERSION, completedStepIds is reset to []. Default true.
  resetOnVersionUpgrade?: boolean;
  // アクセストークン。これを渡さないと 401 になる。
  //
  // このアプリの他の API は utils/RequestUtils が fetch() で
  // Authorization: Bearer を毎回付けている。axios の interceptor は
  // 設定されていないので、生の axios を使うこの hook には何も付かない。
  // (2026-07-28 QA 実測: 他の API は 200 で、ここだけ 401 だった)
  accessToken?: string | null;
}

export interface UseTutorialProgressApi {
  progress: TutorialProgressState;
  loading: boolean;
  error: Error | null;
  // サーバ側にこの機能がまだ無い (404) ことを表す。
  // このとき progress.finishedAt は「完了した」ではなく「出さない」の意味。
  // 呼び出し側は完了扱いしてよいが、完了として記録してはいけない。
  unavailable: boolean;
  refresh(): Promise<void>;
  save(patch: Partial<TutorialProgressState>): Promise<void>;
  markStepComplete(stepId: string): Promise<void>;
  markStarted(): Promise<void>;
  markFinished(): Promise<void>;
  reset(): Promise<void>;
}

// サーバに機能が無いときに返す状態。
// finishedAt を埋めるのは「もう出さない」ことを表すため。
// ★ これは localStorage に書かないし、サーバにも送らない。
//    書くと、あとでサーバ側が動き出したときに全員が完了済みになる。
const SUPPRESSED_STATE: TutorialProgressState = {
  ...EMPTY_STATE,
  finishedAt: "1970-01-01T00:00:00.000Z",
};

function isNotDeployed(e: unknown): boolean {
  // 404 = endpoint そのものが無い = 本番未 deploy。
  // 500 や通信断は「一時的に落ちている」なので区別する
  // (区別しないと、一時障害のたびにチュートリアルが消える)。
  if (!axios.isAxiosError(e)) return false;
  return e.response?.status === 404;
}

function readCache(): TutorialProgressState | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(LOCALSTORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as TutorialProgressState;
  } catch {
    return null;
  }
}

function writeCache(state: TutorialProgressState) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(LOCALSTORAGE_KEY, JSON.stringify(state));
  } catch {
    // localStorage quota / disabled — non-fatal.
  }
}

export function useTutorialProgress(
  options: UseTutorialProgressOptions = {}
): UseTutorialProgressApi {
  const {
    apiBaseUrl = "",
    enableRemote = true,
    resetOnVersionUpgrade = true,
    accessToken = null,
  } = options;

  // ★ object をそのまま useCallback の deps に入れないこと。
  //   毎レンダー別物になるので refresh が作り直され、
  //   useEffect([refresh]) が無限に走る。
  //   deps には文字列の accessToken を入れ、header はこの中で作る。
  const buildHeaders = useCallback(
    () => (accessToken ? { Authorization: `Bearer ${accessToken}` } : undefined),
    [accessToken]
  );

  const [progress, setProgress] = useState<TutorialProgressState>(
    () => readCache() ?? EMPTY_STATE
  );
  const [loading, setLoading] = useState<boolean>(enableRemote);
  const [error, setError] = useState<Error | null>(null);
  const [unavailable, setUnavailable] = useState(false);
  const unavailableRef = useRef(false);

  const progressRef = useRef(progress);
  useEffect(() => {
    progressRef.current = progress;
  }, [progress]);

  const applyState = useCallback((next: TutorialProgressState) => {
    setProgress(next);
    writeCache(next);
  }, []);

  const refresh = useCallback(async () => {
    if (!enableRemote) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await axios.get<TutorialProgressState | null>(
        `${apiBaseUrl}/api/staff/tutorial/progress`,
        { headers: buildHeaders() }
      );
      const server = res.data;
      if (server) {
        const staleVersion =
          resetOnVersionUpgrade && server.version < CURRENT_TUTORIAL_VERSION;
        applyState({
          ...server,
          version: staleVersion ? CURRENT_TUTORIAL_VERSION : server.version,
          completedStepIds: staleVersion ? [] : server.completedStepIds ?? [],
        });
      } else if (!readCache()) {
        // No server record and no cache → seed with empty state.
        applyState(EMPTY_STATE);
      }
      setError(null);
      setUnavailable(false);
      unavailableRef.current = false;
    } catch (e) {
      setError(e as Error);
      if (isNotDeployed(e)) {
        // 本番にこの機能がまだ無い。
        // ここで EMPTY_STATE のまま進むと finishedAt が null なので
        // 全社員にチュートリアルが起動する (2026-07-22 の本番事故はこれ)。
        // 「完了扱い = 出さない」に倒す。localStorage には書かない。
        setUnavailable(true);
        unavailableRef.current = true;
        setProgress(SUPPRESSED_STATE);
        progressRef.current = SUPPRESSED_STATE;
      }
      // 404 以外 (通信断・500) は一時的なものとして、キャッシュの状態を保つ。
    } finally {
      setLoading(false);
    }
  }, [applyState, apiBaseUrl, enableRemote, resetOnVersionUpgrade, buildHeaders]);

  // Initial load.
  useEffect(() => {
    refresh();
  }, [refresh]);

  const save = useCallback(
    async (patch: Partial<TutorialProgressState>) => {
      // サーバに機能が無いと分かっている間は、書き込みも保存もしない。
      // 無い endpoint を叩き続けないため、かつ
      // 「出さないための finishedAt」を完了として残さないため。
      if (unavailableRef.current) return;
      const next: TutorialProgressState = { ...progressRef.current, ...patch };
      applyState(next);
      if (!enableRemote) return;
      try {
        await axios.put(`${apiBaseUrl}/api/staff/tutorial/progress`, next, {
          headers: buildHeaders(),
        });
        setError(null);
      } catch (e) {
        setError(e as Error);
        if (isNotDeployed(e)) {
          setUnavailable(true);
          unavailableRef.current = true;
        }
        // Cached locally regardless; next refresh will attempt reconciliation.
      }
    },
    [applyState, apiBaseUrl, enableRemote, buildHeaders]
  );

  const markStepComplete = useCallback(
    async (stepId: string) => {
      const current = progressRef.current;
      if (current.completedStepIds.includes(stepId)) return;
      await save({
        completedStepIds: [...current.completedStepIds, stepId],
      });
    },
    [save]
  );

  const markStarted = useCallback(async () => {
    if (progressRef.current.startedAt) return;
    await save({ startedAt: new Date().toISOString() });
  }, [save]);

  const markFinished = useCallback(async () => {
    if (progressRef.current.finishedAt) return;
    await save({ finishedAt: new Date().toISOString() });
  }, [save]);

  const reset = useCallback(async () => {
    await save({
      version: CURRENT_TUTORIAL_VERSION,
      completedStepIds: [],
      startedAt: null,
      finishedAt: null,
    });
  }, [save]);

  return {
    progress,
    loading,
    error,
    unavailable,
    refresh,
    save,
    markStepComplete,
    markStarted,
    markFinished,
    reset,
  };
}
